import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

const TILE_CLASS = {
  regular: 'tile',
  wide: 'tile wide',
  tall: 'tile tall',
};

/**
 * Masonry-style gallery using the `.gallery` + `.tile` markup. Click any
 * tile to open the lightbox; arrow keys navigate, Esc closes.
 *
 *   images: [{ id, image, caption?, tileSize? }]
 */
export function Gallery({ images = [], emptyText = 'No photos yet.' }) {
  const [openAt, setOpenAt] = useState(-1);

  const open = (i) => setOpenAt(i);
  const close = useCallback(() => setOpenAt(-1), []);
  const next = useCallback(() => setOpenAt((i) => (i + 1) % images.length), [images.length]);
  const prev = useCallback(() => setOpenAt((i) => (i - 1 + images.length) % images.length), [images.length]);

  useEffect(() => {
    if (openAt < 0) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openAt, next, prev, close]);

  if (!images.length) {
    return <p style={{ color: 'var(--ink-3)', textAlign: 'center', padding: '48px 0' }}>{emptyText}</p>;
  }

  return (
    <>
      <div className="gallery">
        {images.map((img, i) => (
          <button
            key={img.id ?? i}
            type="button"
            className={TILE_CLASS[img.tileSize] || 'tile'}
            style={{ backgroundImage: `url('${img.image}')`, border: 0, padding: 0, cursor: 'zoom-in' }}
            onClick={() => open(i)}
            aria-label={img.caption || `Photo ${i + 1}`}
          />
        ))}
      </div>
      {openAt >= 0 && createPortal(
        <Lightbox image={images[openAt]} onClose={close} onNext={next} onPrev={prev} />,
        document.body,
      )}
    </>
  );
}

function Lightbox({ image, onClose, onNext, onPrev }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(14,42,31,0.92)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute', top: 18, right: 18,
          width: 40, height: 40, borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)', color: '#fff',
          border: 0, fontSize: 22, cursor: 'pointer',
        }}
      >×</button>
      <button
        aria-label="Previous"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        style={arrowStyle('left')}
      >‹</button>
      <figure
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '92vw', maxHeight: '90vh', margin: 0, textAlign: 'center' }}
      >
        <img
          src={image.image}
          alt={image.caption || ''}
          style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 6, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}
        />
        {image.caption && (
          <figcaption style={{ color: 'rgba(255,255,255,0.85)', marginTop: 16, fontSize: 14 }}>
            {image.caption}
          </figcaption>
        )}
      </figure>
      <button
        aria-label="Next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        style={arrowStyle('right')}
      >›</button>
    </div>
  );
}

const arrowStyle = (side) => ({
  position: 'absolute',
  top: '50%', transform: 'translateY(-50%)',
  [side]: 16,
  width: 48, height: 48, borderRadius: '50%',
  background: 'rgba(255,255,255,0.15)', color: '#fff',
  border: 0, fontSize: 28, lineHeight: 1, cursor: 'pointer',
});
